import { Reveal } from "@/components/motion/Reveal";
import {
  UserCog,
  Inbox,
  PenLine,
  MessagesSquare,
  ShieldCheck,
  LayoutDashboard,
} from "lucide-react";

interface Tile {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
}

const DEFAULT_TILES: Tile[] = [
  {
    icon: UserCog,
    title: "Dedicated account manager",
    desc: "One senior operator owns your pipeline. Direct line via the in-portal thread.",
  },
  {
    icon: Inbox,
    title: "Warmed inbox pool",
    desc: "Fresh domains, mailboxes, DNS, and reputation — provisioned and monitored for you.",
  },
  {
    icon: PenLine,
    title: "ICP + copy authored for you",
    desc: "We research your buyer, draft the sequences, and A/B the angles until meetings land.",
  },
  {
    icon: MessagesSquare,
    title: "Reply handling",
    desc: "Every reply is read, classified, and answered by us. Interested prospects reach your calendar.",
  },
  {
    icon: ShieldCheck,
    title: "Deliverability monitored daily",
    desc: "Placement tests, bounce guardrails, and per-inbox capacity so nothing ever burns.",
  },
  {
    icon: LayoutDashboard,
    title: "Client portal access",
    desc: "A read-only dashboard for meetings, pipeline, performance, and messages with your AM.",
  },
];

export function DeliverablesGrid({
  tiles = DEFAULT_TILES,
  eyebrow = "WHAT'S INCLUDED",
  headline = "Every piece of an outbound team, delivered as a service.",
  sub = "One monthly retainer covers the people, the infrastructure, and the software. You get the calendar invites.",
}: {
  tiles?: Tile[];
  eyebrow?: string;
  headline?: string;
  sub?: string;
}) {
  return (
    <section className="relative">
      <div className="max-w-6xl mx-auto px-4 py-20 md:py-28">
        <Reveal stagger={60} className="text-center space-y-3 max-w-2xl mx-auto mb-12">
          <p className="text-[10px] font-mono uppercase tracking-widest text-primary">{eyebrow}</p>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground tracking-tight">{headline}</h2>
          <p className="text-sm md:text-base text-foreground/80">{sub}</p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {tiles.map((t, i) => (
            <Reveal key={t.title} delay={i * 60}>
              <div className="h-full rounded-2xl border border-border/60 bg-card/40 backdrop-blur-xl p-6 space-y-3 hover:border-primary/40 hover:bg-card/60 transition-all">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center">
                  <t.icon className="h-4 w-4 text-primary" />
                </div>
                <h3 className="text-base font-semibold text-foreground">{t.title}</h3>
                <p className="text-sm text-foreground/75 leading-relaxed">{t.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
